import { graphql, useStaticQuery, Link } from "gatsby";
import { GatsbyImage, StaticImage } from "gatsby-plugin-image";
import React, { useEffect, useRef, useState } from "react";
import IconInstagram from "../../assets/IconInstagram";
import IconLinkedIn from "../../assets/IconLinkedIn";
import Button from '../button';
import IconEmployeeOwned from "../../assets/IconEmployeeOwned";
import IconBCorp from "../../assets/IconBCorp";
import iconIso27001 from "../../assets/Icon27001.png";
import Grid, { Col } from "../grid/Grid";
import Section from "../section/Section";
import * as styles from "./Footer.module.css";

const Footer = () => {
  const { contentfulFooter } = useStaticQuery(graphql`
    query {
      contentfulFooter {
        twitter
        linkedIn
        instagram
      }
    }
  `);

  const {
    twitter,
    linkedIn,
    instagram,
  } = contentfulFooter;

  const [fontSize, setFontSize] = useState('4vw');
  const helloWordsRef = useRef(null);
  
  useEffect(() => {
    const calculateFontSize = () => {
      if (helloWordsRef.current) {
        const viewportWidth = window.innerWidth;
        
        // Count characters in each row (including spaces)
        const topRow = "LISBON OLÁ HALLO HI LONDON HEJ MALMÖ"; // 7 words, 6 spaces
        const bottomRow = "NEW YORK HELLO AHOJ TOKYO やあ HOLA"; // 6 words, 5 spaces
        
        // Count actual characters (including spaces)
        const topRowChars = topRow.length; // 35 characters
        const bottomRowChars = bottomRow.length; // 32 characters
        
        // Use the longer row for calculation
        const maxChars = Math.max(topRowChars, bottomRowChars);
        
        // For monospace fonts: 100vw / characterCount = font size needed
        // For proportional fonts: we need to account for average character width
        // Assuming average character width is about 0.6 of font size
        const averageCharWidth = 0.6;
        const targetFontSize = (100 / (maxChars * averageCharWidth));
        
        // Set bounds to prevent extreme sizes
        const clampedFontSize = Math.max(1.5, Math.min(6, targetFontSize));
        
        setFontSize(`${clampedFontSize + 5}vw`);
      }
    };

    // Calculate on mount and resize
    calculateFontSize();
    window.addEventListener('resize', calculateFontSize);
    
    return () => window.removeEventListener('resize', calculateFontSize);
  }, []);
  
  return (
    <footer className={styles.footer}>
      <Section>
        <Grid>
          <Col start={1} span={2}>
            <div>
              <ul className={styles.listLinks}>
                <li><Link to="/what-we-do/" className="smallBodyText">What we do</Link></li>
                <li><Link to="/work/" className="smallBodyText">Work</Link></li>
                <li><Link to="/ai/" className="smallBodyText">AI</Link></li>
                <li><Link to="/blog/" className="smallBodyText">Insights</Link></li>
                <li><Link to="/our-story/" className="smallBodyText">Our story</Link></li>
                <li><Link to="/employee-owned/" className="smallBodyText">Employee owned</Link></li>
                <li><Link to="/b-corp/" className="smallBodyText">B Corp</Link></li>
                <li><Link to="/join-us/" className="smallBodyText">Join us</Link></li>
                <li><Link to="/contact-us/" className="smallBodyText">Contact us</Link></li>
              </ul>
            </div>
          </Col>
          <Col start={3} span={2}>
            <ul className={styles.socialNetworks}>
              <li>
                <a
                  href={instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconInstagram />
                </a>
              </li>
              <li>
                <a
                  href={linkedIn}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <IconLinkedIn />
                </a>
              </li>
            </ul>
          </Col>
          <Col start={5} span={4}>
            <p className={`smallestBodyText ${styles.green}`}>This website is hosted on truly green servers located in regions with renewable energy generation. <Link to="/blog/reducing-the-carbon-footprint-of-our-website/">Find out more</Link></p>
          </Col>
          <Col start={10} span={3}>
            <div className={styles.logos}>
              <IconEmployeeOwned fill="#ffffff" width="130" />
              <IconBCorp fill="#ffffff" width="70" style={{ transform: "translateY(-8px)" }} />
              <img src={iconIso27001} alt="ISO 20071 Information Security Management" style={{ width: "100px" }} />
            </div>
          </Col>
        </Grid>
        <Grid>
          <p className={`${styles.smallPrint} smallestBodyText`}><Link to="/legal/">Legal</Link> | <Link to="/privacy-policy/">Privacy Policy</Link> | <Link to="/accessibility-help/">Accessibility Help</Link><br />Copyright © ustwo Ltd {new Date().getFullYear()}. All rights reserved. </p>
        </Grid>
      </Section>
      <Section className={styles.footerBottom} noInner>
        <div className={styles.helloFooter}>
          <div className={styles.helloWords} ref={helloWordsRef} style={{ fontSize }}>
            <div className={styles.helloRow}>
              <span className={`${styles.helloWord} brandFont`}>LISBON</span>
              <span className={`${styles.helloWord} brandFont`}>OLÁ</span>
              <span className={`${styles.helloWord} brandFont`}>HALLO</span>
              <span className={`${styles.helloWord} brandFont`}>HI</span>
              <span className={`${styles.helloWord} brandFont`}>LONDON</span>
              <span className={`${styles.helloWord} brandFont`}>HEJ</span>
              <span className={`${styles.helloWord} brandFont`}>MALMÖ</span>
            </div>
            <div className={styles.helloRow}>
              <span className={`${styles.helloWord} brandFont`}>NEW YORK</span>
              <span className={`${styles.helloWord} brandFont`}>HELLO</span>
              <span className={`${styles.helloWord} brandFont`}>AHOJ</span>
              <span className={`${styles.helloWord} brandFont`}>TOKYO</span>
              <span className={`${styles.helloWord} brandFont`}>やあ</span>
              <span className={`${styles.helloWord} brandFont`}>HOLA</span>
            </div>
          </div>
          <div className={styles.contactButton}>
            <Button variant="round" to="/contact-us/" ariaLabel="Contact us" symbol="@" invert>Contact us</Button>
          </div>
        </div>
      </Section>
    </footer>
  );
};

export default Footer;
